//Complimentary group item
import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Input,
  Row,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import Flatpickr from "react-flatpickr";
import { Link } from "react-router-dom";
import ComplimentryService from "../../../services/OrderingServices/ComplimentryService";
import CustomDataTable from "../../../Components/CustomTable/CustomTable";

import useTableSearch from "../../../hooks/useTableSearch";
import TableSearch from "../../../Components/Common/TableSearch";

const SummaryCard = ({ title, value, color, icon }) => (
  <Card className="shadow-sm card-animate border-0">
    <CardBody className="d-flex align-items-center justify-content-between">
      <div className="w-100">
        <h6 className="fw-semibold text-uppercase" style={{ color }}>
          {title}
        </h6>
        <div className="d-flex justify-content-between w-100">
          <h4 className="fs-2 fw-bold" style={{ color }}>
            {value}
          </h4>
          <div
            className="rounded-circle d-flex align-items-center justify-content-center"
            style={{
              backgroundColor: color + "20",
              color: color,
              width: "36px",
              height: "36px",
            }}
          >
            <i className={`${icon} fs-5`} />
          </div>
        </div>
      </div>
    </CardBody>
  </Card>
);

const ComplimentryGroupItem = () => {
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    totalData: 0,
  });
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Summary stats
  const [totalLogs, setTotalLogs] = useState(0);
  const [totalQuantity, setTotalQuantity] = useState(0);
  const [uniqueItems, setUniqueItems] = useState(0);

  // filters
  const [dateRange, setDateRange] = useState([null, null]);
  const [filterText, setFilterText] = useState("");

  const formatDate = (date) => {
    if (!date) return null;
    return date.toLocaleDateString("en-CA");
  };

  // Calculate summary stats
  const calculateStats = (data) => {
    if (!data || data.length === 0) {
      setTotalLogs(0);
      setTotalQuantity(0);
      setUniqueItems(0);
      return;
    }

    const flattenedData = data.flatMap((order) =>
      order.items.flatMap((item) =>
        item.logs.map((log) => ({
          itemName: item.itemName,
          quantity: log.quantity,
        }))
      )
    );

    const total = flattenedData.length;
    const qty = flattenedData.reduce(
      (sum, item) => sum + (item.quantity || 0),
      0
    );
    const items = new Set(flattenedData.map((item) => item.itemName)).size;

    setTotalLogs(total);
    setTotalQuantity(qty);
    setUniqueItems(items);
  };

  const fetchData = async (page = 1, pageSize = pagination?.pageSize) => {
    setLoading(true);
    try {
      const response = await ComplimentryService.getGrpItem({
        page,
        pageSize,
        startDate: formatDate(dateRange[0]),
        endDate: formatDate(dateRange[1]),
      });

      console.log("🚀 ~ fetchData ~ response:", response);
      if (response) {
        setAllData(response);
        calculateStats(response);
        setPagination({
          currentPage: response.currentPage,
          pageSize: response.pageSize,
          totalPages: response.totalPages,
          totalData: response.totalData,
        });
      } else {
        setAllData([]);
        setPagination((prev) => ({ ...prev, totalData: 0 }));
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setAllData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1);
  }, [dateRange, filterText]);

  const flattenedData = allData.flatMap((order) =>
    order.items.flatMap((item) =>
      item.logs.map((log) => ({
        orderId: order.orderId,
        orderRefNumber: order.orderRefNumber,
        itemId: item.itemId,
        itemName: item.itemName,
        totalQuantity: item.totalQuantity,
        logId: log.id,
        logQuantity: log.quantity,
        reason: log.reason || "-",
        createdAt: new Date(log.createdAt).toLocaleString(),
        givenBy: log.givenBy,
      }))
    )
  );

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => index + 1,
      width: "75px",
    },
    {
      name: "Order Ref Number",
      selector: (row) => {
        console.log("🚀 ~ ComplimentryGroupItem ~ row:", row);
        return row.orderRefNumber;
      },
      sortable: true,
      cell: (row) => (
        <Link
          to={`/complimentry-logs/detail/${row?.orderRefNumber}`}
          title={row?.orderRefNumber}
        >
          {row?.orderRefNumber}
        </Link>
      ),
    },
    {
      name: "Item Name",
      selector: (row) => row.itemName,
      sortable: true,
      cell: (row) => (
        <Link to={`/item/detail/${row?.itemId}`}>{row.itemName}</Link>
      ),
    },
    {
      name: "Total Qty",
      selector: (row) => row.totalQuantity,
      sortable: true,
      right: true,
      // width: "9vw",
    },
    // {
    //   name: "Log ID",
    //   selector: (row) => row.logId,
    //   sortable: true,
    //   width: "8vw",
    // },
    // {
    //   name: "Log Qty",
    //   selector: (row) => row.logQuantity,
    //   sortable: true,
    //   right: true,
    //   width: "8vw",
    // },
  ];

  const { searchTerm, setSearchTerm, filteredData } = useTableSearch(
    flattenedData,
    ["orderRefNumber", "itemName", "totalQuantity"]
  );
  const getDateRangeDisplay = () => {
    if (!dateRange[0] || !dateRange[1]) return "All Time";
    const start = new Date(dateRange[0]).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    const end = new Date(dateRange[1]).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return `${start} - ${end}`;
  };

  // const filteredData = flattenedData.filter((row) => {
  //   if (!filterText) return true;

  //   const text = filterText.toLowerCase();

  //   return Object.values(row).some((value) =>
  //     String(value || "")
  //       .toLowerCase()
  //       .includes(text)
  //   );
  // });

  return (
    <>
      {/* Summary Cards */}
      {/* <div className="p-3 p-lg-4" style={{ background: "#f8f9fa" }}>
        <Row className="g-3">
          <Col lg={4} md={6} sm={12}>
            <SummaryCard
              title="Total Logs"
              value={totalLogs}
              color="#0d6efd"
              icon="bx bx-list-ul"
            />
          </Col>
          <Col lg={4} md={6} sm={12}>
            <SummaryCard
              title="Total Quantity"
              value={totalQuantity}
              color="#28a745"
              icon="bx bx-bar-chart-alt-2"
            />
          </Col>
          <Col lg={4} md={6} sm={12}>
            <SummaryCard
              title="Unique Items"
              value={uniqueItems}
              color="#ffc107"
              icon="bx bx-package"
            />
          </Col>
        </Row>
      </div> */}

      {/* Main Content */}
      <div className="p-3 p-lg-4">
        {/* Filters */}
        <Row className="mb-3 g-2">
          <Col md={4}>
            <div className="mb-3 d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-1 fw-bold text-dark">Grouped by Items</h5>
                <p className="text-muted small mb-0">{getDateRangeDisplay()}</p>
              </div>
            </div>
          </Col>
          <Col md={4}>
            <TableSearch
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search by all.."
            />
          </Col>
          <Col md={4}>
            <InputGroup>
              <Flatpickr
                options={{ mode: "range", dateFormat: "Y-m-d" }}
                value={dateRange}
                onChange={(dates) => setDateRange(dates)}
                className="form-control"
                placeholder="Select date range"
              />
              <InputGroupText style={{ cursor: "pointer" }}>
                <i className="bx bx-calendar"></i>
              </InputGroupText>
            </InputGroup>
          </Col>
        </Row>

        <hr style={{ borderColor: "#dee2e6", marginTop: "0rem" }} />
        {/* Table */}
        <CustomDataTable
          responsive
          striped
          // pagination
          // paginationServer
          // paginationTotalRows={pagination.totalData}
          // paginationDefaultPage={pagination.currentPage}
          // onChangePage={(page) => fetchData(page)}
          // onChangeRowsPerPage={(newPageSize) => fetchData(1, newPageSize)}
          fixedHeader
          highlightOnHover
          pointerOnHover
          persistTableHead
          progressPending={loading}
          customStyles={{
            headCells: {
              style: {
                fontSize: "0.92rem",
                fontWeight: 610,
              },
            },
            rows: {
              style: {
                fontSize: "0.9rem",
              },
            },
          }}
          progressComponent={
            <div className="my-3 text-center">
              <Triangle visible height="80" width="80" color="#5B71B9" />
              <h5 className="mt-1">Loading...</h5>
            </div>
          }
          columns={columns}
          data={filteredData}
          noDataComponent={
            <div className="text-center my-5 w-100">
              <i className="ri-emotion-unhappy-line display-4 text-secondary" />
              <h5 className="fw-semibold text-secondary">No Data Found</h5>
              <p className="text-muted mb-0">
                No grouped items found for the selected filters.
              </p>
            </div>
          }
        />
      </div>
    </>
  );
};

export default ComplimentryGroupItem;
